import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSyncMobileEdgeChromeFromMenuOpen } from "../../hooks/useSyncMobileEdgeChromeFromMenuOpen";

export function useSceneContextMenuState() {
  const [sceneContextMenuOpen, setSceneContextMenuOpen] = useState(false);
  const [sceneContextMenuPos, setSceneContextMenuPos] = useState({ x: 0, y: 0 });
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [sceneAddJointKindSubmenuOpen, setSceneAddJointKindSubmenuOpen] = useState(false);
  const [sceneAddGeomKindSubmenuOpen, setSceneAddGeomKindSubmenuOpen] = useState(false);
  const [sceneAddVisualGeomKindSubmenuOpen, setSceneAddVisualGeomKindSubmenuOpen] = useState(false);
  const [sceneContextMenuData, setSceneContextMenuData] = useState({
    hasHit: false,
    owningBodyUuid: null,
    primaryTargetType: null,
    priorityPick: null,
    clientPoint: null,
    candidates: {
      object: null,
      body: null,
      mesh: null,
      geom: null,
      inertial: null,
      joint: null,
      site: null,
      camera: null,
    },
  });
  const sceneContextMenuRef = useRef(null);
  const submenuCloseTimeoutRef = useRef(null);
  const isMobile = useIsMobile();

  useSyncMobileEdgeChromeFromMenuOpen(sceneContextMenuOpen);

  const clearSubmenuCloseTimer = () => {
    if (submenuCloseTimeoutRef.current) {
      window.clearTimeout(submenuCloseTimeoutRef.current);
      submenuCloseTimeoutRef.current = null;
    }
  };

  const openSubmenu = (submenuKey) => {
    clearSubmenuCloseTimer();
    setActiveSubmenu(submenuKey);
    if (submenuKey !== "add") {
      setSceneAddJointKindSubmenuOpen(false);
      setSceneAddGeomKindSubmenuOpen(false);
      setSceneAddVisualGeomKindSubmenuOpen(false);
    }
  };

  const closeSubmenuSoon = () => {
    clearSubmenuCloseTimer();
    submenuCloseTimeoutRef.current = window.setTimeout(() => {
      setActiveSubmenu(null);
      setSceneAddJointKindSubmenuOpen(false);
      setSceneAddGeomKindSubmenuOpen(false);
      setSceneAddVisualGeomKindSubmenuOpen(false);
      submenuCloseTimeoutRef.current = null;
    }, 160);
  };

  useEffect(() => {
    if (!sceneContextMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (sceneContextMenuRef.current && !sceneContextMenuRef.current.contains(event.target)) {
        setActiveSubmenu(null);
        setSceneContextMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveSubmenu(null);
        setSceneContextMenuOpen(false);
      }
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [sceneContextMenuOpen]);

  // Long-press context menu on touch synthesizes a click under the finger; drop that first in-menu
  // activation so users choose an action deliberately (also avoids hover-opened submenus receiving it).
  useLayoutEffect(() => {
    if (!sceneContextMenuOpen || !isMobile) return undefined;
    let ate = false;
    const onClickCapture = (event) => {
      if (ate) return;
      const menuEl = sceneContextMenuRef.current;
      if (!menuEl || !menuEl.contains(event.target)) return;
      ate = true;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
    };
    document.addEventListener("click", onClickCapture, true);
    const timeoutId = window.setTimeout(() => {
      document.removeEventListener("click", onClickCapture, true);
    }, 400);
    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, [sceneContextMenuOpen, isMobile]);

  useEffect(() => {
    if (sceneContextMenuOpen) return;
    clearSubmenuCloseTimer();
    setActiveSubmenu(null);
    setSceneAddJointKindSubmenuOpen(false);
    setSceneAddGeomKindSubmenuOpen(false);
    setSceneAddVisualGeomKindSubmenuOpen(false);
  }, [sceneContextMenuOpen]);

  return {
    activeSubmenu,
    closeSubmenuSoon,
    openSubmenu,
    sceneAddGeomKindSubmenuOpen,
    sceneAddJointKindSubmenuOpen,
    sceneAddVisualGeomKindSubmenuOpen,
    sceneContextMenuData,
    sceneContextMenuOpen,
    sceneContextMenuPos,
    sceneContextMenuRef,
    setActiveSubmenu,
    setSceneAddGeomKindSubmenuOpen,
    setSceneAddJointKindSubmenuOpen,
    setSceneAddVisualGeomKindSubmenuOpen,
    setSceneContextMenuData,
    setSceneContextMenuOpen,
    setSceneContextMenuPos,
  };
}
